import { useState } from 'react';
import { Link } from 'react-router';
import { BackBar } from '@/components/BackBar';
import { BookIcon, ChevronIcon, MinusIcon, PlusIcon } from '@/components/Icons';
import { StarBadge } from '@/components/Lantern';
import { Screen } from '@/components/Screen';
import { CALCULATION_METHODS, MADHABS, type MadhabName } from '@bonyaan/prayer-times';
import { LOCALES, LOCALE_NAMES } from '@/i18n/types';
import { useAppState } from '@/state/useAppState';
import { useT } from '@/state/useT';
import {
  ACCENTS,
  ACCENT_HUES,
  KIDS_MAX_AGE,
  clampAge,
  isOpenEndedAge,
  skinForAge,
  type Accent,
  type SchemeChoice,
  type Skin,
} from '@/theme/types';

const SCHEME_CHOICES: SchemeChoice[] = ['day', 'night', 'auto'];

export function Settings() {
  const {
    profile,
    preferences,
    skin,
    scheme,
    timesProvider,
    saveProfile,
    updatePreferences,
    updateTimes,
  } = useAppState();
  const { t, number } = useT();
  const [locating, setLocating] = useState<'idle' | 'busy' | 'denied'>('idle');

  const times = preferences.times;
  const located = times.coordinates !== null;

  const askForLocation = () => {
    if (!navigator.geolocation) {
      setLocating('denied');
      return;
    }
    setLocating('busy');
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocating('idle');
        void updateTimes({
          coordinates: {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          },
        });
      },
      // Refusing is a normal answer, not an error state to shout about: the
      // sample times stay and the app keeps working.
      () => setLocating('denied'),
      { timeout: 10_000, maximumAge: 60 * 60 * 1000 },
    );
  };

  // Clamped for display too: a profile carrying an age above the ceiling
  // reads as the open-ended step rather than as a number the stepper cannot
  // reach.
  const age = clampAge(profile?.age ?? 8);
  const openEndedAge = isOpenEndedAge(age);
  // The open-ended top step reads "14+" everywhere it appears, not as the 15
  // that stands behind it — the number below the row and the number in it
  // saying different things is how a person stops trusting either.
  const ageDisplay = openEndedAge
    ? t('onboarding.agePlus', { age: number(KIDS_MAX_AGE) })
    : number(age);
  const byAge = skinForAge(age);
  const chosenByAge = preferences.skinOverride === null;
  const versionWord = (s: Skin) => t(s === 'kids' ? 'settings.fun' : 'settings.plain');

  const setAge = (next: number) => {
    if (!profile) return;
    // Changing the age re-decides the skin, so it clears any manual override.
    void saveProfile({ name: profile.name, age: clampAge(next) });
    void updatePreferences({ skinOverride: null });
  };

  return (
    <Screen quietSky header={<BackBar title={t('settings.title')} />}>
      <div className="sect" style={{ marginTop: 18 }}>
        {t('settings.whoIsPraying')}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
        <Link to="/onboarding" className="row">
          <span style={{ flexGrow: 1 }}>
            <span className="rowlbl">{t('settings.name')}</span>
            <span className="rowval" style={{ display: 'block', marginTop: 1 }}>
              {profile?.name || t('settings.nameUnset')}
            </span>
          </span>
          <ChevronIcon />
        </Link>
        <div className="row">
          <span style={{ flexGrow: 1 }}>
            <span className="rowlbl">{t('settings.age')}</span>
            <span className="rowval" style={{ display: 'block', marginTop: 1 }}>
              {openEndedAge
                ? t('onboarding.yearsPlus', { age: number(KIDS_MAX_AGE) })
                : t('onboarding.years', { count: age })}
            </span>
          </span>
          <button
            type="button"
            className="stepb sm"
            aria-label={t('onboarding.younger')}
            onClick={() => setAge(age - 1)}
          >
            <MinusIcon size={20} />
          </button>
          <button
            type="button"
            className="stepb sm"
            aria-label={t('onboarding.older')}
            onClick={() => setAge(age + 1)}
          >
            <PlusIcon size={20} />
          </button>
        </div>
      </div>

      <div className="sect" style={{ marginTop: 20 }}>
        {t('settings.language')}
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
        {LOCALES.map((locale) => (
          <button
            key={locale}
            type="button"
            lang={locale}
            data-testid={`locale-${locale}`}
            className={`pill${preferences.locale === locale ? ' on' : ''}`}
            aria-pressed={preferences.locale === locale}
            onClick={() => void updatePreferences({ locale })}
          >
            {LOCALE_NAMES[locale]}
          </button>
        ))}
      </div>

      <div className="sect" style={{ marginTop: 20 }}>
        {t('settings.prayerTimes')}
      </div>
      <div className="muted-note" style={{ marginTop: 5 }} data-testid="times-status">
        {locating === 'denied'
          ? t('settings.locationDenied')
          : located
            ? t('settings.locationSet', {
                lat: number(Math.round(times.coordinates!.latitude * 1000) / 1000),
                lon: number(Math.round(times.coordinates!.longitude * 1000) / 1000),
              })
            : t('settings.sampleTimes')}
      </div>
      <button
        type="button"
        className="btn-g"
        data-testid="use-location"
        style={{ marginTop: 8 }}
        disabled={locating === 'busy'}
        onClick={askForLocation}
      >
        {locating === 'busy'
          ? t('settings.locating')
          : t(located ? 'settings.updateLocation' : 'settings.useLocation')}
      </button>

      <label className="sect" htmlFor="method" style={{ display: 'block', marginTop: 14 }}>
        {t('settings.method')}
      </label>
      <select
        id="method"
        className="select"
        data-testid="method"
        style={{ marginTop: 8 }}
        value={times.method}
        onChange={(e) =>
          void updateTimes({ method: e.target.value as (typeof CALCULATION_METHODS)[number] })
        }
      >
        {CALCULATION_METHODS.map((method) => (
          <option key={method} value={method}>
            {method.replace(/([a-z])([A-Z])/g, '$1 $2')}
          </option>
        ))}
      </select>
      <div className="muted-note" style={{ marginTop: 7 }}>
        {t('settings.methodNote')}
      </div>

      <div className="sect" style={{ marginTop: 14 }}>
        {t('settings.madhab')}
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
        {MADHABS.map((madhab: MadhabName) => (
          <button
            key={madhab}
            type="button"
            data-testid={`madhab-${madhab}`}
            className={`pill${times.madhab === madhab ? ' on' : ''}`}
            aria-pressed={times.madhab === madhab}
            onClick={() => void updateTimes({ madhab })}
          >
            {t(`settings.madhab.${madhab}`)}
          </button>
        ))}
      </div>
      <div className="muted-note" style={{ marginTop: 7 }} data-testid="times-provider">
        {timesProvider.id}
      </div>

      <div className="sect" style={{ marginTop: 20 }}>
        {t('settings.lookAndFeel')}
      </div>
      <div className="muted-note" style={{ marginTop: 5 }}>
        {t(chosenByAge ? 'settings.byAge' : 'settings.byHand', {
          age: ageDisplay,
          version: versionWord(byAge),
        })}
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
          gap: 10,
          marginTop: 8,
        }}
      >
        <button
          type="button"
          data-testid="skin-kids"
          className={`card${skin === 'kids' ? ' on' : ''}`}
          aria-pressed={skin === 'kids'}
          onClick={() => void updatePreferences({ skinOverride: 'kids' })}
        >
          <StarBadge size={26} />
          <div
            style={{
              fontFamily: 'var(--fd)',
              fontSize: 16.5,
              fontWeight: 800,
              lineHeight: 1.2,
              marginTop: 4,
            }}
          >
            {t('settings.fun')}
          </div>
          <div
            style={{ fontSize: 11, lineHeight: 1.25, color: 'var(--panel-dim)', fontWeight: 600 }}
          >
            {t('settings.funSub')}
          </div>
        </button>
        <button
          type="button"
          data-testid="skin-adult"
          className={`card${skin === 'adult' ? ' on' : ''}`}
          aria-pressed={skin === 'adult'}
          onClick={() => void updatePreferences({ skinOverride: 'adult' })}
        >
          <BookIcon />
          <div
            style={{
              fontFamily: 'var(--fd)',
              fontSize: 16.5,
              fontWeight: 800,
              lineHeight: 1.2,
              marginTop: 4,
            }}
          >
            {t('settings.plain')}
          </div>
          <div
            style={{ fontSize: 11, lineHeight: 1.25, color: 'var(--panel-dim)', fontWeight: 600 }}
          >
            {t('settings.plainSub')}
          </div>
        </button>
      </div>
      {chosenByAge ? null : (
        <button
          type="button"
          className="btn-g"
          data-testid="skin-by-age"
          style={{ marginTop: 9 }}
          onClick={() => void updatePreferences({ skinOverride: null })}
        >
          {t('settings.backToAge', { version: versionWord(byAge) })}
        </button>
      )}

      <div className="sect" style={{ marginTop: 20 }}>
        {t('settings.lightDark')}
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
        {SCHEME_CHOICES.map((choice) => (
          <button
            key={choice}
            type="button"
            data-testid={`scheme-${choice}`}
            className={`pill${preferences.scheme === choice ? ' on' : ''}`}
            aria-pressed={preferences.scheme === choice}
            onClick={() => void updatePreferences({ scheme: choice })}
          >
            {choice === 'auto' && preferences.scheme === 'auto'
              ? t('settings.autoResolved', {
                  scheme: t(scheme === 'day' ? 'settings.day' : 'settings.night'),
                })
              : t(`settings.${choice}`)}
          </button>
        ))}
      </div>
      <div className="muted-note" style={{ marginTop: 7 }}>
        {t('settings.autoNote')}
      </div>

      <div className="sect" style={{ marginTop: 20 }}>
        {t('settings.themeColour')}
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
        {ACCENTS.map((accent: Accent) => (
          <button
            key={accent}
            type="button"
            data-testid={`accent-${accent}`}
            className={`swatch${preferences.accent === accent ? ' on' : ''}`}
            aria-label={t(`accent.${accent}`)}
            aria-pressed={preferences.accent === accent}
            style={{ background: `oklch(var(--ai-l) var(--ai-c) ${ACCENT_HUES[accent]})` }}
            onClick={() => void updatePreferences({ accent })}
          >
            <svg className="tick" width={18} height={18} viewBox="0 0 20 20" aria-hidden="true">
              <path
                d="M5 10.3 8.2 13.5 15 6.5"
                fill="none"
                stroke={`oklch(.985 .02 ${ACCENT_HUES[accent]})`}
                strokeWidth="2.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        ))}
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 7, marginTop: 10 }}>
        <span style={{ fontFamily: 'var(--fd)', fontSize: 16, fontWeight: 700, lineHeight: 1.2 }}>
          {t(`accent.${preferences.accent}`)}
        </span>
        <span className="muted-note">{t('settings.accentNote')}</span>
      </div>

      <div className="muted-note" style={{ marginTop: 20 }}>
        {t('settings.parentGate', { age: number(KIDS_MAX_AGE) })}
      </div>
    </Screen>
  );
}
